/** biome-ignore-all lint/correctness/useHookAtTopLevel: intentional */
"use client";

import { AxisBottom, AxisLeft } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { scaleLinear, scaleTime } from "@visx/scale";
import { AreaClosed, Bar, LinePath } from "@visx/shape";
import { isAfter, isBefore, isEqual, sub } from "date-fns";
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useEventListener, useScrollLock } from "../../../hooks/external";
import { useDateRangeFormatter } from "../../../hooks/useDateRangeFormatter";
import { classNames } from "../../../utils";
import { ChartTooltip } from "../ChartTooltip";
// Reuse the existing helper for matching standalone column data
import { getCorrespondingVolumeData } from "./getCorrespondingVolumeData";
import { TimeSeriesChartEmptyState } from "./TimeSeriesChartEmptyState";
import { TimeSeriesChartSkeleton } from "./TimeSeriesChartSkeleton";
// Keep a small bottom margin so X-axis labels have space to render
const DEFAULT_MARGIN = {
  top: 0,
  right: 0,
  bottom: 24,
  left: 0
};
export const TimeSeriesChart = ({
  className,
  data,
  getDate,
  getValue,
  getScatterValue,
  getIsOutlier,
  getColumnValue,
  standaloneColumn,
  renderTooltip: RenderTooltip,
  onHover,
  onTimeRangeChange,
  overrides,
  tooltipDelay,
  scatterOptions,
  yAxisMin,
  yAxisMax,
  lineType = "line",
  areaOptions,
  hoverDotColor,
  live,
  crosshair,
  annotations
}) => {
  const getDefaultDomain = () => {
    // Ensure the time domain always has width to avoid NaN scales
    const expandByMs = 60_000; // 1 minute padding on each side
    if (data.length === 0) {
      const now = Date.now();
      return [new Date(now - expandByMs), new Date(now + expandByMs)];
    }
    const min = getDate(data[0]);
    const lastElement = data.at(-1);
    if (!lastElement) {
      const now_0 = Date.now();
      return [new Date(now_0 - expandByMs), new Date(now_0 + expandByMs)];
    }
    const max = getDate(lastElement);
    if (min.getTime() === max.getTime()) {
      const t = min.getTime();
      return [new Date(t - expandByMs), new Date(t + expandByMs)];
    }
    return [min, max];
  };
  const [visibleDomain, setVisibleDomain] = useState(getDefaultDomain());
  const handleVisibleDomainChange = domain => {
    setVisibleDomain(domain);
    onTimeRangeChange?.(domain[0], domain[1]);
  };
  const visibleData = useMemo(() => {
    // Include extra data point on each side to ensure the chart is not cut off
    let minIndex = data.findIndex(d => getDate(d) >= visibleDomain[0]) - 1;
    if (minIndex < 0) {
      minIndex = 0;
    }
    let maxIndex = data.findIndex(d_0 => getDate(d_0) > visibleDomain[1]);
    if (maxIndex === -1) {
      maxIndex = data.length - 1;
    }
    return data.slice(minIndex, maxIndex + 1);
  }, [data, visibleDomain]);

  // Long-press detection for touch to lock vertical scroll
  const longTouchTimerRef = useRef(null);
  const isTouchActiveRef = useRef(false);
  const {
    lock: lockScroll,
    unlock: unlockScroll
  } = useScrollLock({
    autoLock: false
  });
  const hasScrollLockedRef = useRef(false);

  // Ensure scroll is unlocked if component unmounts mid-touch
  useEffect(() => {
    return () => {
      if (longTouchTimerRef.current !== null) {
        window.clearTimeout(longTouchTimerRef.current);
        longTouchTimerRef.current = null;
      }
      if (hasScrollLockedRef.current) {
        unlockScroll();
        hasScrollLockedRef.current = false;
      }
      isTouchActiveRef.current = false;
    };
  }, []);
  const idBase = useId();
  const areaGradientId = `${idBase}-area-gradient`;
  // Early empty render to avoid errors
  const hasLine = Boolean(getValue);
  const hasScatter = Boolean(getScatterValue);
  const hasInlineColumns = Boolean(getColumnValue);
  const hasStandaloneColumns = Boolean(standaloneColumn && standaloneColumn.items.length > 0);
  const visibleAnnotations = useMemo(() => {
    if (!(annotations && visibleDomain)) {
      return [];
    }
    return annotations.filter(annotation => {
      return isAfter(annotation.date, visibleDomain[0]) && isBefore(annotation.date, visibleDomain[1]);
    });
  }, [annotations, visibleDomain]);
  return <div className={classNames("relative overflow-visible", {
    "touch-pan-x touch-pan-y select-none": Boolean(onTimeRangeChange)
  }, className)}>
      <ParentSize>
        {({
        width,
        height
      }) => {
        // Check if axes are hidden to adjust margins accordingly
        const xAxisHidden = Boolean(overrides?.XAxis?.hidden ?? overrides?.XAxis?.hide);
        const margin = {
          top: 0,
          right: 0,
          bottom: xAxisHidden ? 0 : DEFAULT_MARGIN.bottom,
          left: 0
        };
        const innerWidth = Math.max(0, width - margin.left - margin.right);
        const innerHeight = Math.max(0, height - margin.top - margin.bottom);

        // Reserve up to 20% of the height visually for columns, but overlay them within the same chart
        const columnHeightRatio = 0.2;
        const mainChartHeight = Math.max(0, innerHeight);
        const overlayTop = Math.max(0, mainChartHeight * (1 - columnHeightRatio));

        // Build x domain from union of dates in main data (required)
        const xDomain = useMemo(() => {
          if (visibleDomain) {
            return visibleDomain;
          }
          return getDefaultDomain();
        }, [visibleData, getDate, visibleDomain]);

        // Single full-width time scale (simpler and consistent)
        const xScale = useMemo(() => scaleTime({
          range: [0, innerWidth],
          domain: xDomain
        }), [innerWidth, xDomain]);

        // Content-only scale with slight edge padding so marks don't hug edges
        const CONTENT_PAD = 4;
        const contentWidth = Math.max(0, innerWidth - CONTENT_PAD * 2);
        const xScaleContent = useMemo(() => scaleTime({
          range: [CONTENT_PAD, CONTENT_PAD + contentWidth],
          domain: xDomain
        }), [contentWidth, xDomain]);

        // Full data domain (used to clamp panning)
        const fullDataDomain = useMemo(() => {
          if (data.length === 0) {
            return getDefaultDomain();
          }
          const first = getDate(data[0]);
          const lastItem = data.at(-1);
          if (!lastItem) {
            return getDefaultDomain();
          }
          const last = getDate(lastItem);
          return first.getTime() <= last.getTime() ? [first, last] : [last, first];
        }, [data, getDate]);
        const defaultDateFormatter = useDateRangeFormatter(xDomain[0], xDomain[1]);

        // Clamp a proposed [start, end] domain so that at least 10% of the window
        // overlaps the actual data range (prevents fully panning off-screen)
        const clampPannedDomain = useCallback(proposed => {
          const dataStartMs = fullDataDomain[0].getTime();
          const dataEndMs = fullDataDomain[1].getTime();
          const startMs = proposed[0].getTime();
          const endMs = proposed[1].getTime();
          const windowWidth = Math.max(1, endMs - startMs);
          const dataRange = Math.max(0, dataEndMs - dataStartMs);

          // Require at least 10% of the window to overlap, but not more than data range
          const minRequiredOverlap = Math.min(Math.floor(windowWidth * 0.1), dataRange);

          // Compute allowable start bounds
          const minStart = dataStartMs - (windowWidth - minRequiredOverlap);
          const maxStart = dataEndMs - minRequiredOverlap;
          const clampedStart = Math.min(Math.max(startMs, minStart), maxStart);
          const clampedEnd = clampedStart + windowWidth;
          return [new Date(clampedStart), new Date(clampedEnd)];
        }, [fullDataDomain]);

        // Validate that a proposed zoom range would show at least 10% of all data
        // (from proposed start to end of all data should be at least 10% of total range)
        const validateZoomRange = useCallback((proposed_0, current) => {
          const dataStartMs_0 = fullDataDomain[0].getTime();
          const dataEndMs_0 = fullDataDomain[1].getTime();
          const totalDataRange = Math.max(1, dataEndMs_0 - dataStartMs_0);
          const proposedStartMs = proposed_0[0].getTime();
          const proposedEndMs = proposed_0[1].getTime();
          const proposedRange = proposedEndMs - proposedStartMs;
          const currentStartMs = current[0].getTime();
          const currentEndMs = current[1].getTime();
          const currentRange = currentEndMs - currentStartMs;
          const rangeFromStartToEnd = Math.max(0, dataEndMs_0 - proposedStartMs);

          // Check if the range from proposed start to end of data is at least 10% of total
          const minRequired = totalDataRange * 0.1;
          const isValid = rangeFromStartToEnd >= minRequired;

          // If currently invalid and zooming out (increasing range), allow it
          if (!isValid && proposedRange > currentRange) {
            return true;
          }
          return isValid;
        }, [fullDataDomain]);

        // Main Y scale (for line/scatter)
        const yMainDomain = useMemo(() => {
          const minOverride = yAxisMin ?? overrides?.YAxis?.yAxisMin;
          const maxOverride = yAxisMax ?? overrides?.YAxis?.yAxisMax;
          const values = [];
          if (hasLine) {
            for (const d_1 of visibleData) {
              const v = getValue?.(d_1);
              if (typeof v === "number") {
                values.push(v);
              }
            }
          }
          if (hasScatter) {
            for (const d_2 of visibleData) {
              const v_0 = getScatterValue?.(d_2);
              if (typeof v_0 === "number") {
                values.push(v_0);
              }
            }
          }
          if (values.length === 0) {
            return [minOverride ?? 0, maxOverride ?? 1];
          }
          const dataMin = Math.min(...values);
          const dataMax = Math.max(...values);
          const baseLower = typeof minOverride === "number" ? minOverride : dataMin;
          const baseUpper = typeof maxOverride === "number" ? maxOverride : dataMax;

          // Always add small padding so scatter circles near the edges are fully visible
          const baseRange = Math.max(0, baseUpper - baseLower);
          const pad = baseRange * 0.1 || 1;
          const paddedLower = hasScatter ? baseLower - pad : Math.max(0, baseLower - pad);
          const paddedUpper = baseUpper + pad;
          return [paddedLower, paddedUpper];
        }, [visibleData, getValue, getScatterValue, hasLine, hasScatter, yAxisMin, yAxisMax, overrides?.YAxis?.yAxisMin, overrides?.YAxis?.yAxisMax]);
        const yMainScale = useMemo(() => scaleLinear({
          range: [mainChartHeight, 0],
          domain: yMainDomain,
          nice: true
        }), [mainChartHeight, yMainDomain]);

        // Inline column Y scale (columns rendered within main chart area under the line)
        const yInlineColumnScale = useMemo(() => {
          if (!hasInlineColumns) {
            return null;
          }
          const vals = visibleData.map(d_3 => getColumnValue?.(d_3)).filter(v_1 => typeof v_1 === "number");
          const max_0 = vals.length > 0 ? Math.max(...vals) : 1;
          return scaleLinear({
            range: [mainChartHeight, overlayTop],
            domain: [0, max_0],
            nice: true
          });
        }, [visibleData, getColumnValue, hasInlineColumns, mainChartHeight, overlayTop]);

        // Standalone column Y scale (columns at chart bottom)
        const yStandaloneColumnScale = useMemo(() => {
          if (hasStandaloneColumns && standaloneColumn) {
            const vals_0 = standaloneColumn.items.map(d_4 => standaloneColumn.getValue(d_4)).filter(v_2 => typeof v_2 === "number");
            const max_1 = vals_0.length > 0 ? Math.max(...vals_0) : 1;
            return scaleLinear({
              range: [mainChartHeight, overlayTop],
              domain: [0, max_1],
              nice: true
            });
          }
          return null;
        }, [hasStandaloneColumns, standaloneColumn, mainChartHeight, overlayTop]);
        const [hover, setHover] = useState(null);
        const prevHoverKeyRef = useRef(null);

        // Panning state
        const [isPanning, setIsPanning] = useState(false);
        const [isDragInitiated, setIsDragInitiated] = useState(false);
        const panStartRef = useRef(null);
        const DRAG_THRESHOLD = 5; // pixels

        // Pinch zoom tracking state
        const pinchStartRef = useRef(null);

        // CMD-drag selection state
        const [isSelecting, setIsSelecting] = useState(false);
        useEventListener("keydown", event => {
          if (event.key === "Meta") {
            setIsSelecting(true);
          }
        });
        useEventListener("keyup", event_0 => {
          if (event_0.key === "Meta") {
            setIsSelecting(false);
          }
        });
        const [selectionStartX, setSelectionStartX] = useState(null);
        const [selectionCurrentX, setSelectionCurrentX] = useState(null);
        const handlePointerMove = (clientX, clientY, currentTarget) => {
          // Compute coordinates relative to the overlay rect (inside translated Group)
          const rect = currentTarget.getBoundingClientRect();
          const xPos = clientX - rect.left;
          const yPos = clientY - rect.top;

          // Update selection box when selecting and prevent hover/pan
          if (isSelecting) {
            const clampedX = Math.max(0, Math.min(innerWidth, xPos));
            setSelectionCurrentX(clampedX);
            return;
          }

          // Handle panning threshold and actual panning
          if (isDragInitiated && panStartRef.current && onTimeRangeChange) {
            const deltaX = xPos - panStartRef.current.x;

            // Check if we've moved beyond the drag threshold to start actual panning
            if (!isPanning && Math.abs(deltaX) > DRAG_THRESHOLD) {
              setIsPanning(true);
            }

            // Perform panning if we're in panning mode
            if (isPanning) {
              const pixelsPerMs = innerWidth / (xDomain[1].getTime() - xDomain[0].getTime());
              const timeDelta = deltaX / pixelsPerMs;
              const originalStart = panStartRef.current.timeRange[0].getTime();
              const originalEnd = panStartRef.current.timeRange[1].getTime();
              const [newStart, newEnd] = clampPannedDomain([new Date(originalStart - timeDelta), new Date(originalEnd - timeDelta)]);
              handleVisibleDomainChange([newStart, newEnd]);
            }

            // Always return early during any drag state to prevent hover
            return;
          }
          if (xPos < 0 || xPos > innerWidth || yPos < 0 || yPos > innerHeight) {
            setHover(null);
            return;
          }

          // Find the closest point across all series (scatter and line)
          let closestItem;
          let closestDist = Number.POSITIVE_INFINITY;
          let closestSeries;
          let closestY;

          // Check all scatter points
          if (hasScatter && getScatterValue) {
            for (const d_5 of visibleData) {
              const yVal = getScatterValue(d_5);
              if (typeof yVal !== "number" || Number.isNaN(yVal)) {
                continue;
              }
              const px = xScaleContent(getDate(d_5));
              const py = yMainScale(yVal);
              const dx = xPos - px;
              const dy = yPos - py;
              const dist = Math.hypot(dx, dy);
              if (dist < closestDist) {
                closestDist = dist;
                closestItem = d_5;
                closestSeries = "scatter";
                closestY = yVal;
              }
            }
          }

          // Check all line/area points
          if (hasLine && getValue) {
            for (const d_6 of visibleData) {
              const yVal_0 = getValue(d_6);
              if (typeof yVal_0 !== "number" || Number.isNaN(yVal_0)) {
                continue;
              }
              const px_0 = xScaleContent(getDate(d_6));
              const dx_0 = Math.abs(xPos - px_0);
              // For charts with only a line/area, snap by X only
              // If scatter exists too, preserve Euclidean distance logic
              const dist_0 = hasScatter ? Math.hypot(xPos - px_0, yPos - yMainScale(yVal_0)) : dx_0;
              if (dist_0 < closestDist) {
                closestDist = dist_0;
                closestItem = d_6;
                closestSeries = "line";
                closestY = yVal_0;
              }
            }
          }

          // If we found a closest point, use it
          if (closestItem && closestSeries && typeof closestY === "number") {
            const hoverX = margin.left + xScaleContent(getDate(closestItem));
            const hoverY = margin.top + yMainScale(closestY);
            setHover({
              x: hoverX,
              y: hoverY,
              item: closestItem,
              series: closestSeries
            });
            return;
          }

          // No valid data to hover
          setHover(null);
        };
        const onMouseMove = evt => {
          handlePointerMove(evt.clientX, evt.clientY, evt.currentTarget);
        };
        const onTouchMove = evt_0 => {
          if (evt_0.touches.length === 2 && onTimeRangeChange) {
            // Two-finger pinch/pan gesture
            const touch1 = evt_0.touches[0];
            const touch2 = evt_0.touches[1];

            // Calculate pinch distance
            const dx_1 = touch2.clientX - touch1.clientX;
            const dy_0 = touch2.clientY - touch1.clientY;
            const distance = Math.hypot(dx_1, dy_0);

            // Compute gesture center (clamped to content area)
            const centerX = (touch1.clientX + touch2.clientX) / 2;
            const rect_0 = evt_0.currentTarget.getBoundingClientRect();
            const xPos_0 = centerX - rect_0.left;
            const contentMin = CONTENT_PAD;
            const contentMax = CONTENT_PAD + contentWidth;
            const clampedCenterX = Math.max(contentMin, Math.min(contentMax, xPos_0));
            const centerTime = xScaleContent.invert(clampedCenterX);

            // Initialize pinch state on first two-finger frame
            if (!pinchStartRef.current) {
              pinchStartRef.current = {
                distance,
                timeRange: [xDomain[0], xDomain[1]],
                centerTime,
                centerX
              };
            }
            const startMs_0 = pinchStartRef.current.timeRange[0].getTime();
            const endMs_0 = pinchStartRef.current.timeRange[1].getTime();
            const originalRange = endMs_0 - startMs_0;

            // Zoom factor from pinch distance ratio
            const ratio = distance / pinchStartRef.current.distance;
            // Increase distance => zoom in (smaller range)
            const desiredRange = originalRange / (ratio || 1);

            // Clamp zoom range
            const DEFAULT_DOMAIN = getDefaultDomain();
            const maxRange = (DEFAULT_DOMAIN[1].getTime() - DEFAULT_DOMAIN[0].getTime()) * 2;
            const minRange = 5 * 60 * 1000; // 5 minutes minimum window
            const clampedRange = Math.max(minRange, Math.min(desiredRange, maxRange));
            const zoomCenterMs = pinchStartRef.current.centerTime.getTime();
            const zoomStart = new Date(zoomCenterMs - clampedRange / 2);
            const zoomEnd = new Date(zoomCenterMs + clampedRange / 2);
            const shift = pinchStartRef.current.centerX - centerX;
            const shiftPercent = shift / rect_0.width;
            const shiftMs = shiftPercent * clampedRange;
            const proposedDomain = clampPannedDomain([new Date(zoomStart.getTime() + shiftMs), new Date(zoomEnd.getTime() + shiftMs)]);

            // Validate that zoom would show at least 10% of all data
            if (!validateZoomRange(proposedDomain, xDomain)) {
              return;
            }

            // Apply zoom around center (with clamping)
            handleVisibleDomainChange(proposedDomain);

            // Prevent browser/page pinch-zoom
            evt_0.preventDefault();

            // Avoid hover while pinching
            setHover(null);
            setIsDragInitiated(true);
            setIsPanning(true);
            return;
          }
          if (evt_0.touches.length === 1) {
            // Single touch for hover
            const touch = evt_0.touches[0];
            handlePointerMove(touch.clientX, touch.clientY, evt_0.currentTarget);
          }
        };
        const onTouchStart = evt_1 => {
          if (evt_1.touches.length === 2 && onTimeRangeChange) {
            // Clear hover immediately when two-finger pan gesture starts
            setHover(null);

            // Two-finger touch panning
            const touch1_0 = evt_1.touches[0];
            const touch2_0 = evt_1.touches[1];
            const centerX_0 = (touch1_0.clientX + touch2_0.clientX) / 2;
            const rect_1 = evt_1.currentTarget.getBoundingClientRect();
            const xPos_1 = centerX_0 - rect_1.left;
            setIsDragInitiated(true);
            panStartRef.current = {
              x: xPos_1,
              timeRange: [xDomain[0], xDomain[1]]
            };
            if (!pinchStartRef.current) {
              pinchStartRef.current = {
                distance: Math.hypot(touch1_0.clientX - touch2_0.clientX, touch1_0.clientY - touch2_0.clientY),
                timeRange: [xDomain[0], xDomain[1]],
                centerTime: xScaleContent.invert(centerX_0),
                centerX: centerX_0
              };
            }
          } else if (evt_1.touches.length === 1) {
            // Single touch for hover
            const touch_0 = evt_1.touches[0];
            handlePointerMove(touch_0.clientX, touch_0.clientY, evt_1.currentTarget);
            // Start long-press timer (500ms) to lock vertical window scroll while interacting
            isTouchActiveRef.current = true;
            if (longTouchTimerRef.current !== null) {
              window.clearTimeout(longTouchTimerRef.current);
            }
            longTouchTimerRef.current = window.setTimeout(() => {
              if (isTouchActiveRef.current && !hasScrollLockedRef.current) {
                lockScroll();
                hasScrollLockedRef.current = true;
              }
            }, 500);
          }
        };
        const onMouseLeave = () => {
          setHover(null);
          // End panning if mouse leaves the chart area
          if (isPanning) {
            setIsPanning(false);
            panStartRef.current = null;
          }
          // Cancel selection if active
          if (isSelecting) {
            setIsSelecting(false);
            setSelectionStartX(null);
            setSelectionCurrentX(null);
          }
        };
        const onMouseDown = evt_2 => {
          if (!onTimeRangeChange) {
            return;
          }

          // Clear hover immediately when pan gesture starts
          setHover(null);

          // Initiate drag tracking (actual panning starts after threshold)
          const rect_2 = evt_2.currentTarget.getBoundingClientRect();
          const xPos_2 = evt_2.clientX - rect_2.left;

          // If CMD/meta key is held, start selection instead of panning
          if (evt_2.metaKey) {
            setIsSelecting(true);
            const startX = Math.max(0, Math.min(innerWidth, xPos_2));
            setSelectionStartX(startX);
            setSelectionCurrentX(startX);
            // Ensure panning does not start
            setIsDragInitiated(false);
            panStartRef.current = null;
            return;
          }
          setIsDragInitiated(true);
          panStartRef.current = {
            x: xPos_2,
            timeRange: [xDomain[0], xDomain[1]]
          };
        };
        const onMouseUp = () => {
          // Finalize selection if active
          if (isSelecting && selectionStartX !== null && selectionCurrentX !== null && onTimeRangeChange) {
            const startX_0 = Math.min(selectionStartX, selectionCurrentX);
            const endX = Math.max(selectionStartX, selectionCurrentX);

            // Constrain to content area in pixels
            const contentMin_0 = CONTENT_PAD;
            const contentMax_0 = CONTENT_PAD + contentWidth;
            const clampedStart_0 = Math.max(contentMin_0, Math.min(contentMax_0, startX_0));
            const clampedEnd_0 = Math.max(contentMin_0, Math.min(contentMax_0, endX));

            // Only apply if selection exceeds threshold
            if (clampedEnd_0 - clampedStart_0 > DRAG_THRESHOLD) {
              const startDate = xScaleContent.invert(clampedStart_0);
              const endDate = xScaleContent.invert(clampedEnd_0);
              handleVisibleDomainChange([startDate, endDate]);
            }

            // Clear selection state
            setIsSelecting(false);
            setSelectionStartX(null);
            setSelectionCurrentX(null);
            // Also clear any pan state
            setIsPanning(false);
            setIsDragInitiated(false);
            panStartRef.current = null;
            return;
          }
          if (isPanning || isDragInitiated) {
            setIsPanning(false);
            setIsDragInitiated(false);
            panStartRef.current = null;
          }
        };
        const onTouchEnd = () => {
          // End panning if we were panning
          if (isPanning || isDragInitiated) {
            setIsPanning(false);
            setIsDragInitiated(false);
            panStartRef.current = null;
          }

          // Clear pinch state
          pinchStartRef.current = null;

          // Clear hover and release any scroll lock from long-press
          setHover(null);
          isTouchActiveRef.current = false;
          if (longTouchTimerRef.current !== null) {
            window.clearTimeout(longTouchTimerRef.current);
            longTouchTimerRef.current = null;
          }
          if (hasScrollLockedRef.current) {
            unlockScroll();
            hasScrollLockedRef.current = false;
          }
        };

        // Ref for the chart rect to attach native wheel event listener
        const chartRectRef = useRef(null);

        // Zoom in/out time window on mouse wheel while hovering the chart
        const handleWheel = useCallback(evt_3 => {
          if (!onTimeRangeChange) {
            return;
          }

          // Clear hover state when zooming starts
          setHover(null);

          // Prevent page scroll when interacting with chart
          evt_3.preventDefault();
          const startMs_1 = xDomain[0].getTime();
          const endMs_1 = xDomain[1].getTime();
          const currentRange_0 = endMs_1 - startMs_1;

          // Determine zoom direction and amount based on deltaY
          // Positive deltaY = scroll down = zoom out
          // Negative deltaY = scroll up = zoom in
          const zoomDirection = evt_3.deltaY > 0 ? 1 : -1;

          // Base zoom factor (10% per scroll step)
          const baseZoomFactor = 0.1;
          // Scale zoom amount based on deltaY magnitude (normalized to reasonable range)
          const zoomAmount = Math.min(Math.abs(evt_3.deltaY) / 100, 2) * baseZoomFactor;
          const zoomFactor = 1 + zoomDirection * zoomAmount;

          // Calculate new range with zoom limits
          const newRange = currentRange_0 * zoomFactor;

          // Set reasonable zoom limits
          const DEFAULT_DOMAIN_0 = getDefaultDomain();
          const maxRange_0 = (DEFAULT_DOMAIN_0[1].getTime() - DEFAULT_DOMAIN_0[0].getTime()) * 2;
          const minRange_0 = 5 * 60 * 1000; // 5 minute minimum
          const clampedRange_0 = Math.max(minRange_0, Math.min(newRange, maxRange_0));
          const newStart_0 = sub(endMs_1, {
            seconds: clampedRange_0 / 1000
          });
          const proposedDomain_0 = [newStart_0, new Date(endMs_1)];

          // Validate that zoom would show at least 10% of all data
          if (!validateZoomRange(proposedDomain_0, xDomain)) {
            return;
          }

          // Only update if the range actually changed
          if (newStart_0.getTime() !== startMs_1) {
            handleVisibleDomainChange(proposedDomain_0);
          }
        }, [onTimeRangeChange, xDomain, handleVisibleDomainChange, setHover, validateZoomRange]);
        const onDoubleClick = () => {
          handleVisibleDomainChange(getDefaultDomain());
        };

        // Attach native wheel event listener with passive: false
        useEffect(() => {
          const chartRect = chartRectRef.current;
          if (!chartRect) {
            return;
          }
          if (!onTimeRangeChange) {
            return;
          }

          // Add wheel event listener with passive: false to allow preventDefault
          chartRect.addEventListener("wheel", handleWheel, {
            passive: false
          });
          return () => {
            chartRect.removeEventListener("wheel", handleWheel);
          };
        }, [handleWheel, onTimeRangeChange]);

        // Pre-compute derived series
        const lineSeries = useMemo(() => {
          if (hasLine && getValue) {
            return visibleData.map(d_7 => ({
              x: getDate(d_7),
              y: getValue(d_7)
            }));
          }
          return [];
        }, [visibleData, getDate, getValue, hasLine]);
        const scatterSeries = useMemo(() => {
          if (hasScatter && getScatterValue) {
            const series = visibleData.map(d_8 => {
              const yVal_1 = getScatterValue(d_8);
              if (typeof yVal_1 !== "number") {
                return null;
              }
              return {
                x: getDate(d_8),
                y: yVal_1,
                item: d_8,
                isOutlier: getIsOutlier?.(d_8) ?? false
              };
            }).filter(p => Boolean(p));
            return series;
          }
          return [];
        }, [visibleData, getDate, getScatterValue, hasScatter, getIsOutlier]);
        const inlineColumns = useMemo(() => {
          if (hasInlineColumns && getColumnValue) {
            return visibleData.map(d_9 => ({
              x: getDate(d_9),
              y: getColumnValue(d_9)
            }));
          }
          return [];
        }, [visibleData, getColumnValue, getDate, hasInlineColumns]);
        const standaloneColumns = useMemo(() => {
          if (hasStandaloneColumns && standaloneColumn) {
            return standaloneColumn.items.map(d_10 => ({
              x: standaloneColumn.getDate(d_10),
              y: standaloneColumn.getValue(d_10)
            }));
          }
          return [];
        }, [hasStandaloneColumns, standaloneColumn]);
        const matchedStandaloneForHover = useMemo(() => {
          if (hover && hasStandaloneColumns && standaloneColumn) {
            return getCorrespondingVolumeData(getDate(hover.item), standaloneColumn);
          }
          return undefined;
        }, [hover, hasStandaloneColumns, standaloneColumn, getDate]);

        // Notify external consumers when the hover target changes
        useEffect(() => {
          if (!onHover) {
            return;
          }
          // When hover clears, emit null exactly once
          if (!hover) {
            if (prevHoverKeyRef.current !== null) {
              onHover(null);
              prevHoverKeyRef.current = null;
            }
            return;
          }

          // Build a stable key based on series + timestamp + value to reduce duplicate calls
          const timeKey = getDate(hover.item).getTime();
          const yVal_2 = hover.series === "scatter" ? getScatterValue?.(hover.item) : getValue?.(hover.item);
          const valueKey = typeof yVal_2 === "number" && Number.isFinite(yVal_2) ? yVal_2 : "nv";
          const nextKey = `${hover.series}:${timeKey}:${valueKey}`;
          if (prevHoverKeyRef.current !== nextKey) {
            onHover({
              item: hover.item,
              series: hover.series,
              x: hover.x,
              y: hover.y,
              matchedColumnData: matchedStandaloneForHover
            });
            prevHoverKeyRef.current = nextKey;
          }
        }, [hover, matchedStandaloneForHover, onHover, getDate, getScatterValue, getValue]);

        // Compute final point for liveness marker when not hovered
        const lastVisiblePoint = useMemo(() => {
          if (!live) {
            return null;
          }
          if (lineSeries.length > 0) {
            const p_0 = lineSeries.at(-1);
            if (!p_0) {
              return null;
            }
            return {
              x: p_0.x,
              y: p_0.y,
              isOutlier: false,
              series: "line"
            };
          }
          if (scatterSeries.length > 0) {
            const s = scatterSeries.at(-1);
            if (!s) {
              return null;
            }
            return {
              x: s.x,
              y: s.y,
              isOutlier: s.isOutlier,
              series: "scatter"
            };
          }
          return null;
        }, [live, lineSeries, scatterSeries]);
        const lastDataPoint = data.at(-1);
        const isLastVisiblePointLatest = lastVisiblePoint && lastDataPoint && isEqual(lastVisiblePoint.x, getDate(lastDataPoint));
        const safeId = useMemo(() => idBase.replace(/[^a-zA-Z0-9_-]/g, "_"), [idBase]);
        const livePingClass = `live-ping-${safeId}`;
        const livePingKeyframes = `live-ping-${safeId}`;
        const areaClipId = `${idBase}-area-clip`;
        const areaClipRightId = `${idBase}-area-clip-right`;
        const dotsMaskPatternId = `${idBase}-dots-mask-pattern`;
        const dotsMaskId = `${idBase}-dots-mask`;
        return <>
              {live ? <style>{`
@keyframes ${livePingKeyframes} {
  0% { transform: scale(1); opacity: 0.9; }
  70% { opacity: 0.4; }
  100% { transform: scale(2.25); opacity: 0; }
}
.${livePingClass} {
  transform-origin: center;
  transform-box: fill-box;
  animation: ${livePingKeyframes} 1.25s ease-out infinite;
}
`}</style> : null}
              <svg aria-label="timeseries-chart-v2" height={height} role="img" width={width}>
                <Group left={0} top={0}>
                  {/* no grids */}

                  {/* inline columns under line area */}
                  {yInlineColumnScale && inlineColumns.length > 0 ? (() => {
                const xs = inlineColumns.map(d_11 => xScaleContent(d_11.x));
                let minGap = Number.POSITIVE_INFINITY;
                for (let i = 1; i < xs.length; i += 1) {
                  const gap = Math.abs(xs[i] - xs[i - 1]);
                  if (gap > 0 && gap < minGap) {
                    minGap = gap;
                  }
                }
                if (!Number.isFinite(minGap)) {
                  minGap = contentWidth / 10;
                }
                const barWidth = Math.max(1, minGap * 0.8);
                return inlineColumns.map((d_12, i_0) => {
                  const cx = xScaleContent(d_12.x);
                  // Constrain bar position to stay within chart bounds
                  const idealBarX = cx - barWidth / 2;
                  const minBarX = CONTENT_PAD;
                  const maxBarX = CONTENT_PAD + contentWidth - barWidth;
                  const barX = Math.max(minBarX, Math.min(maxBarX, idealBarX));
                  const barY = yInlineColumnScale(d_12.y);
                  const barH = Math.max(0, mainChartHeight - barY);
                  return <Bar fill="rgb(var(--color-blue-3))" fillOpacity={0.25} height={barH} key={`inline-bar-${d_12.x.getTime()}-${i_0}`} stroke="rgb(var(--color-blue-3))" strokeWidth={0.5} width={barWidth} x={barX} y={barY} />;
                });
              })() : null}

                  {/* gradient definition for area chart */}
                  {lineType === "area" && areaOptions?.gradient ? <LinearGradient from={areaOptions.gradient.from} fromOpacity={areaOptions.gradient.fromOpacity ?? 0.8} id={areaGradientId} to={areaOptions.gradient.to} toOpacity={areaOptions.gradient.toOpacity ?? 0.1} /> : null}

                  {/* main line or area */}
                  {lineSeries.length > 0 ? lineType === "area" ? <>
                        {crosshair ? <defs>
                            <clipPath id={areaClipId}>
                              <rect height={mainChartHeight} width={Math.max(0, Math.min(innerWidth, hover ? hover.x - margin.left : innerWidth))} x={0} y={0} />
                            </clipPath>
                            <clipPath id={areaClipRightId}>
                              <rect height={mainChartHeight} width={Math.max(0, innerWidth - Math.max(0, hover ? hover.x - margin.left : innerWidth))} x={Math.max(0, hover ? hover.x - margin.left : innerWidth)} y={0} />
                            </clipPath>
                            {/* Dotted alpha mask so the gradient shows through */}
                            <pattern height={6} id={dotsMaskPatternId} patternUnits="userSpaceOnUse" width={6}>
                              <circle cx={1.5} cy={1.5} fill="white" r={1} />
                            </pattern>
                            <mask height={mainChartHeight} id={dotsMaskId} maskUnits="userSpaceOnUse" width={innerWidth} x={0} y={0}>
                              <rect fill={`url(#${dotsMaskPatternId})`} height={mainChartHeight} width={innerWidth} />
                            </mask>
                          </defs> : null}
                        <AreaClosed clipPath={crosshair ? `url(#${areaClipId})` : undefined} data={lineSeries} fill={areaOptions?.gradient ? `url(#${areaGradientId})` : areaOptions?.fillColor ?? "rgb(var(--color-blue-3))"} fillOpacity={areaOptions?.gradient ? 1 : areaOptions?.fillOpacity ?? 0.3} pointerEvents="none" x={d_13 => xScaleContent(d_13.x)} y={d_14 => yMainScale(d_14.y)} yScale={yMainScale} />
                        {crosshair ? <AreaClosed clipPath={`url(#${areaClipRightId})`} data={lineSeries} fill={areaOptions?.gradient ? `url(#${areaGradientId})` : areaOptions?.fillColor ?? "rgb(var(--color-blue-3))"} fillOpacity={areaOptions?.gradient ? 1 : areaOptions?.fillOpacity ?? 0.3} mask={`url(#${dotsMaskId})`} pointerEvents="none" x={d_15 => xScaleContent(d_15.x)} y={d_16 => yMainScale(d_16.y)} yScale={yMainScale} /> : null}
                        {(() => {
                  const stroke = areaOptions?.strokeColor ?? "rgb(var(--color-blue-4))";
                  if (crosshair && hover) {
                    const boundary = getDate(hover.item).getTime();
                    const left = lineSeries.filter(p_1 => p_1.x.getTime() <= boundary);
                    const right = lineSeries.filter(p_2 => p_2.x.getTime() >= boundary);
                    return <>
                                <LinePath data={left} pointerEvents="none" stroke={stroke} strokeOpacity={1} strokeWidth={areaOptions?.strokeWidth ?? 2} x={d_17 => xScaleContent(d_17.x)} y={d_18 => yMainScale(d_18.y)} />
                                <LinePath data={right} pointerEvents="none" stroke={stroke} strokeOpacity={0.5} strokeWidth={areaOptions?.strokeWidth ?? 2} x={d_19 => xScaleContent(d_19.x)} y={d_20 => yMainScale(d_20.y)} />
                              </>;
                  }
                  return <LinePath data={lineSeries} pointerEvents="none" stroke={stroke} strokeWidth={areaOptions?.strokeWidth ?? 2} x={d_21 => xScaleContent(d_21.x)} y={d_22 => yMainScale(d_22.y)} />;
                })()}
                      </> : (() => {
                const stroke_0 = "rgb(var(--color-blue-4))";
                if (crosshair && hover) {
                  const boundary_0 = getDate(hover.item).getTime();
                  const left_0 = lineSeries.filter(p_3 => p_3.x.getTime() <= boundary_0);
                  const right_0 = lineSeries.filter(p_4 => p_4.x.getTime() > boundary_0);
                  return <>
                              <LinePath data={left_0} pointerEvents="none" stroke={stroke_0} strokeOpacity={1} strokeWidth={2} x={d_23 => xScaleContent(d_23.x)} y={d_24 => yMainScale(d_24.y)} />
                              <LinePath data={right_0} pointerEvents="none" stroke={stroke_0} strokeOpacity={0.5} strokeWidth={2} x={d_25 => xScaleContent(d_25.x)} y={d_26 => yMainScale(d_26.y)} />
                            </>;
                }
                return <LinePath data={lineSeries} pointerEvents="none" stroke={stroke_0} strokeWidth={2} x={d_27 => xScaleContent(d_27.x)} y={d_28 => yMainScale(d_28.y)} />;
              })() : null}

                  {/* scatter points - render AFTER columns so dots sit above volume bars */}
                  {/* Moved below columns section to ensure higher z-index than bars */}

                  {/* axes with optional overrides (similar to DepthChart) */}
                  {(() => {
                const yAxisOverrides = overrides?.YAxis;
                const xAxisOverrides = overrides?.XAxis;
                const yAxisHidden = Boolean(yAxisOverrides?.hidden ?? yAxisOverrides?.hide);
                const xAxisHidden_0 = Boolean(xAxisOverrides?.hidden ?? xAxisOverrides?.hide);
                const {
                  hidden: _yh,
                  hide: _yh2,
                  yAxisMin: _yMin,
                  yAxisMax: _yMax,
                  ...yAxisPass
                } = yAxisOverrides ?? {};
                const {
                  hidden: _xh,
                  hide: _xh2,
                  ...xAxisPass
                } = xAxisOverrides ?? {};
                return <>
                        {yAxisHidden ? null : <AxisLeft hideAxisLine={yAxisPass.hideAxisLine ?? true} hideTicks left={0} numTicks={(() => {
                    // Responsive Y ticks based on height - reduced for cleaner display
                    const h = mainChartHeight;
                    return yAxisPass.numTicks ?? (h < 120 ? 2 : h < 200 ? 3 : 4);
                  })()} scale={yMainScale} tickLabelProps={yAxisPass.tickLabelProps ?? (() => ({
                    className: "font-mono text-[12px] fill-text-secondary",
                    textAnchor: "start",
                    dx: 6
                  }))} tickLength={0} {...yAxisPass} />}
                        {xAxisHidden_0 ? null : (() => {
                    // Derive a reasonable desired tick count by width
                    const desiredCount = xAxisPass.numTicks ?? (innerWidth < 260 ? 2 : innerWidth < 420 ? 3 : innerWidth < 600 ? 4 : innerWidth < 800 ? 6 : 8);

                    // Build a denser set of candidates so we can filter by label width
                    const candidateTicks = xScale.ticks(Math.max(2, desiredCount * 3));
                    const axisTickFormatter = xAxisPass.tickFormat ?? (value => defaultDateFormatter(new Date(value)));

                    // Estimate label widths using a conservative average per character for 12px mono
                    // Loosened to reduce chance of overlap further
                    const AVERAGE_CHAR_WIDTH_PX = 8;
                    const SAFETY_SCALE = 1.25;
                    const MIN_LABEL_WIDTH_PX = 28;
                    const MIN_GAP_PX = 16; // extra breathing room between labels

                    const filteredTickValues = [];
                    let lastRight = Number.NEGATIVE_INFINITY;
                    for (const value_0 of candidateTicks) {
                      const t_0 = value_0;
                      // Use default formatter for measuring text width to avoid type issues
                      const label = String(defaultDateFormatter(new Date(t_0)));
                      const approxWidth = Math.max(MIN_LABEL_WIDTH_PX, Math.ceil(label.length * AVERAGE_CHAR_WIDTH_PX * SAFETY_SCALE));
                      const x = xScale(t_0);
                      const left_1 = x - approxWidth / 2;
                      const right_1 = x + approxWidth / 2;
                      if (lastRight === Number.NEGATIVE_INFINITY && x < approxWidth / 2) {
                        continue;
                      }
                      if (left_1 - lastRight >= MIN_GAP_PX) {
                        filteredTickValues.push(t_0);
                        lastRight = right_1;
                      }
                    }
                    return <AxisBottom hideAxisLine={xAxisPass.hideAxisLine ?? true} hideTicks scale={xScale} tickFormat={axisTickFormatter} tickLabelProps={(_value, index) => {
                      const provided = xAxisPass.tickLabelProps;
                      if (provided && typeof provided === "function") {
                        return provided(_value, index, []);
                      }
                      if (provided && typeof provided !== "function") {
                        return provided;
                      }
                      return {
                        className: "font-mono text-[12px] fill-text-secondary",
                        textAnchor: "middle",
                        dx: 0,
                        dy: 8
                      };
                    }} tickLength={0} tickValues={filteredTickValues} top={innerHeight} {...xAxisPass} />;
                  })()}
                      </>;
              })()}

                  {/* standalone columns overlaid at bottom of main area (beneath scatter) */}
                  {hasStandaloneColumns && yStandaloneColumnScale ? (() => {
                const xs_0 = standaloneColumns.map(d_29 => xScaleContent(d_29.x));
                let minGap_0 = Number.POSITIVE_INFINITY;
                for (let i_1 = 1; i_1 < xs_0.length; i_1 += 1) {
                  const gap_0 = Math.abs(xs_0[i_1] - xs_0[i_1 - 1]);
                  if (gap_0 > 0 && gap_0 < minGap_0) {
                    minGap_0 = gap_0;
                  }
                }
                if (!Number.isFinite(minGap_0)) {
                  minGap_0 = contentWidth / 10;
                }
                const barWidth_0 = Math.max(1, minGap_0 * 0.8);
                const bars = standaloneColumns.map((d_30, i_2) => {
                  const cx_0 = xScaleContent(d_30.x);
                  // Constrain bar position to stay within chart bounds
                  const idealBarX_0 = cx_0 - barWidth_0 / 2;
                  const minBarX_0 = CONTENT_PAD;
                  const maxBarX_0 = CONTENT_PAD + contentWidth - barWidth_0;
                  const barX_0 = Math.max(minBarX_0, Math.min(maxBarX_0, idealBarX_0));
                  const barY_0 = yStandaloneColumnScale(d_30.y);
                  const barH_0 = Math.max(0, mainChartHeight - barY_0);
                  return <Bar fill="rgb(var(--color-bg-additional-3))" height={barH_0} key={`standalone-bar-${d_30.x.getTime()}-${i_2}`} stroke="rgb(var(--color-bg-additional-3))" strokeWidth={0.5} width={barWidth_0} x={barX_0} y={barY_0} />;
                });
                return bars;
              })() : null}

                  {/* scatter points - must render AFTER columns to sit on top visually */}
                  {scatterSeries.map((d_31, i_3) => {
                const isHovered = Boolean(hover && hover.series === "scatter" && getScatterValue && getDate(hover.item).getTime() === d_31.x.getTime() && getScatterValue(hover.item) === d_31.y);
                if (isHovered) {
                  const hoverFill = d_31.isOutlier ? "rgb(var(--color-warning))" : "rgb(var(--color-blue-3))";
                  return <circle cx={xScaleContent(d_31.x)} cy={yMainScale(d_31.y)} fill={hoverFill} key={`scatter-${i_3}`} pointerEvents="none" r={4} />;
                }
                const strokeColor = d_31.isOutlier ? "rgb(var(--color-warning))" : "rgb(var(--color-blue-3))";
                return <circle cx={xScaleContent(d_31.x)} cy={yMainScale(d_31.y)} fill="none" key={`scatter-${i_3}`} pointerEvents="none" r={3} stroke={strokeColor} strokeWidth={1} />;
              })}

                  {/* hover indicator and optional crosshair */}
                  {hover ? (() => {
                const isOutlierHover = hover.series === "scatter" && (getIsOutlier?.(hover.item) ?? false);
                const fillColor = isOutlierHover ? "rgb(var(--color-warning))" : hoverDotColor || "rgb(var(--color-blue-3))";
                const cx_1 = hover.x - margin.left;
                const cy = hover.y - margin.top;
                return <>
                            {crosshair ? <g pointerEvents="none">
                                <line stroke={areaOptions?.strokeColor ?? "rgb(var(--color-blue-3))"} strokeDasharray="3,3" strokeLinecap="round" strokeOpacity={0.8} strokeWidth={1.5} x1={cx_1} x2={cx_1} y1={0} y2={mainChartHeight} />
                              </g> : null}
                            <circle cx={cx_1} cy={cy} fill={fillColor} pointerEvents="none" r={4} />
                          </>;
              })() : null}

                  {/* live liveness marker on the final point when not hovered */}
                  {!hover && live && lastVisiblePoint && isLastVisiblePointLatest ? <g pointerEvents="none">
                      {(() => {
                  const isOutlierLive = lastVisiblePoint.isOutlier;
                  const fillColor_0 = isOutlierLive ? "rgb(var(--color-warning))" : hoverDotColor || "rgb(var(--color-blue-3))";
                  const cx_2 = xScaleContent(lastVisiblePoint.x);
                  const cy_0 = yMainScale(lastVisiblePoint.y);
                  return <>
                            <circle cx={cx_2} cy={cy_0} fill={fillColor_0} r={4} />
                            <circle className={livePingClass} cx={cx_2} cy={cy_0} fill={fillColor_0} fillOpacity={0.5} r={6} />
                          </>;
                })()}
                    </g> : null}

                  {/* selection overlay when CMD-dragging */}
                  {isSelecting && selectionStartX !== null && selectionCurrentX !== null ? (() => {
                const startX_1 = Math.min(selectionStartX, selectionCurrentX);
                const endX_0 = Math.max(selectionStartX, selectionCurrentX);
                const contentMin_1 = CONTENT_PAD;
                const contentMax_1 = CONTENT_PAD + contentWidth;
                const clampedStart_1 = Math.max(contentMin_1, Math.min(contentMax_1, startX_1));
                const clampedEnd_1 = Math.max(contentMin_1, Math.min(contentMax_1, endX_0));
                const overlayX = clampedStart_1;
                const overlayW = Math.max(0, clampedEnd_1 - clampedStart_1);
                return <rect fill="rgb(var(--color-blue-3))" fillOpacity={0.12} height={mainChartHeight} pointerEvents="none" stroke="rgb(var(--color-blue-3))" strokeDasharray="4,3" strokeOpacity={0.5} width={overlayW} x={overlayX} y={0} />;
              })() : null}

                  {/* interaction layer for main area (must be last to capture events above all marks) */}
                  <rect fill="transparent" height={mainChartHeight} onDoubleClick={onDoubleClick} onMouseDown={onMouseDown} onMouseLeave={onMouseLeave} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onTouchCancel={onTouchEnd} onTouchEnd={onTouchEnd} onTouchMove={onTouchMove} onTouchStart={onTouchStart} pointerEvents="all" ref={chartRectRef} style={{
                cursor: isPanning ? "grabbing" : isSelecting ? "crosshair" : onTimeRangeChange ? "grab" : "default"
              }} width={innerWidth} />
                </Group>
              </svg>
              {/* Tooltip using portal - must be outside SVG to position correctly */}
              {RenderTooltip && hover ? <ChartTooltip animation="none"
          // Allow delaying tooltip open to reduce flicker
          // Use scatter-specific delay if hovering scatter, otherwise general delay
          content={<RenderTooltip item={hover.item} matchedColumnData={matchedStandaloneForHover} series={hover.series} />} delay={hover.series === "scatter" && scatterOptions?.tooltipDelay ? scatterOptions.tooltipDelay : tooltipDelay} hover={{
            x: hover.x,
            y: hover.y
          }} /> : null}

              {/* Annotations positioned absolutely over the chart */}
              {visibleAnnotations?.map((annotation_0, i_4) => {
            const cx_3 = margin.left + xScaleContent(annotation_0.date);
            const cy_1 = annotation_0.align === "value" && annotation_0.value !== undefined ? margin.top + yMainScale(annotation_0.value) : margin.top + mainChartHeight - 16; // Default to bottom of chart (with 16px margin)

            return <Fragment key={`annotation-${annotation_0.date.getTime()}-${i_4}`}>
                    <div aria-hidden style={{
                position: "absolute",
                left: cx_3,
                top: margin.top,
                height: mainChartHeight,
                transform: "translateX(-0.5px)",
                pointerEvents: "none",
                borderLeft: "1px dashed",
                borderLeftColor: annotation_0.value !== undefined && annotation_0.value > 0 ? "rgb(var(--color-success-3))" : "rgb(var(--color-error-3))",
                opacity: 0.5
              }} />
                    <div style={{
                position: "absolute",
                left: cx_3,
                top: cy_1,
                transform: "translate(-50%, -50%)",
                pointerEvents: "auto",
                width: "max-content"
              }}>
                      {annotation_0.content}
                    </div>
                  </Fragment>;
          })}
            </>;
      }}
      </ParentSize>
    </div>;
};
TimeSeriesChart.Skeleton = TimeSeriesChartSkeleton;
TimeSeriesChart.EmptyState = TimeSeriesChartEmptyState;